import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';

import { useAppDispatch } from 'store/store';
import { setReportAnIssueMode } from 'store/ticket-service/ticketSlice';
import { sendReport } from 'store/user-service/asyncActions';

import { NamespacesList } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';

import { ReactComponent as ArrowLeft } from 'assets/icons/arrow-left.svg';
import { ReactComponent as CloseSmall } from 'assets/icons/close_small.svg';
import { ReactComponent as DocumentUpload } from 'assets/icons/document-upload.svg';

import { LeaveReportAnIssueModal } from './LeaveReportAnIssueModal';
import { SuccessBugReportModal } from './SuccessBugReportModal';

interface FormValues {
	message: string;
}

export const ReportAnIssue = () => {
	const { t: commonTranslate, i18n } = useTranslation(NamespacesList['common']);
	const { t: reportIssueTranslate } = useTranslation(NamespacesList['reportIssuePage']);

	const dispatch = useAppDispatch();

	const storedUser = localStorage.getItem('user');
	const user = storedUser ? JSON.parse(storedUser) : null;

	const [option, setOption] = useState('');
	const [category, setCategory] = useState('');
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [isSuccessBugReportModal, setIsSuccessBugReportModal] = useState<boolean>(false);
	const [isLeaveReportAnIssueModal, setIsLeaveReportAnIssueModal] = useState<boolean>(false);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newFiles = Array.from(e.target.files as FileList).filter((file) =>
			file.type.includes('image/')
		);

		const remainingSlots = 5 - selectedFiles.length;
		const filesToAdd = newFiles.slice(0, remainingSlots);

		if (newFiles.length > remainingSlots) {
			errorNotify(
				commonTranslate('errors.fileLimitTitle'),
				commonTranslate('errors.fileLimitDescription')
			);
		}

		setSelectedFiles((prevFiles) => [...prevFiles, ...filesToAdd]);
	};

	const handleRemoveFile = (indexToRemove: number) => {
		setSelectedFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
	};

	const handleOptionChange = (e: SelectChangeEvent) => {
		setOption(e.target.value as string);
	};

	const handleCategoryChange = (e: SelectChangeEvent) => {
		setCategory(e.target.value as string);
	};

	const convertFileToBase64 = (file: File) => {
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const base64Data = reader.result?.toString().split(',')[1];
				resolve(base64Data || '');
			};
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	const sendReportAnIssue = async (data: any) => {
		if (!data.message.trim() && selectedFiles.length === 0) {
			errorNotify(
				commonTranslate('errors.missingInformationErrorTitle'),
				commonTranslate('errors.missingInformationErrorDescription')
			);
			return;
		}

		try {
			setLoading(true);

			const base64JpgFiles = await Promise.all(
				selectedFiles.map(async (file) => {
					return await convertFileToBase64(file);
				})
			);

			const reportData = {
				userEmail: user.email,
				userText: data.message,
				option: option,
				category: category,
				base64JpgFiles: base64JpgFiles,
			};

			await dispatch(sendReport(reportData)).then(() => {
				setLoading(false);
				setIsSuccessBugReportModal(true);
			});
		} catch (error) {
			console.error(commonTranslate('errors.customBugSubmitErrorTitle'), error);
		}
	};

	const { handleSubmit, register, reset, getValues } = useForm<FormValues>({
		defaultValues: {
			message: '',
		},
	});

	const handleCloseModal = () => {
		setIsSuccessBugReportModal(false);
		setIsLeaveReportAnIssueModal(false);
	};

	const resetForm = () => {
		reset();
		setOption('');
		setCategory('');
		setSelectedFiles([]);
	};

	const leaveReportAnIssue = () => {
		const formValues = getValues();

		if (option.length || category.length || selectedFiles.length || formValues.message.trim())
			setIsLeaveReportAnIssueModal(true);
		else dispatch(setReportAnIssueMode(false));
	};

	useEffect(() => {
		return () => {
			dispatch(setReportAnIssueMode(false));
		};
	}, []);

	return (
		<Box
			className="rtl-able"
			sx={{
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				position: 'relative',
			}}
		>
			<Button
				className="not-rtl-able"
				sx={{
					position: 'absolute',
					left: '0',
					textTransform: 'none',
					fontWeight: '700',
					display: 'flex',
					gap: '4px',
				}}
				onClick={leaveReportAnIssue}
			>
				<ArrowLeft />
				{commonTranslate('buttons.back')}
			</Button>
			<Box
				sx={{
					marginTop: { xs: '30px', md: '0' },
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '4px',
				}}
			>
				<Typography
					sx={{
						fontSize: '24px',
						fontWeight: '700',
					}}
				>
					{reportIssueTranslate('text.reportIssueTitle')}
				</Typography>

				<Typography
					sx={{
						fontSize: '14px',
						fontWeight: '400',
						color: '#707A8E',
					}}
				>
					{reportIssueTranslate('text.reportIssueDescription')}
				</Typography>
			</Box>

			<form
				onSubmit={handleSubmit(sendReportAnIssue)}
				style={{
					marginTop: '20px',
					width: '100%',
					height: 'calc(100% - 81px)',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
				}}
			>
				<Box
					sx={{
						width: '100%',
						height: 'calc(100% - 60px)',
						display: 'flex',
						gap: '24px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignSelf: 'center',
							gap: '12px',
							width: '100%',
							height: '100%',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '20px',
							}}
						>
							<Typography sx={{ whiteSpace: 'nowrap' }}>
								{reportIssueTranslate('text.issueTypeSelectTitle')}
							</Typography>

							<FormControl fullWidth size="small">
								<InputLabel
									shrink={false}
									id="select-label"
									sx={{ color: '#9FA6B3', right: i18n?.language === 'ar' ? '30px' : 'unset' }}
								>
									{option === '' && commonTranslate('inputs.reportIssueTypePlaceholder')}
								</InputLabel>
								<Select
									data-testid="option-select"
									labelId="select-label"
									id="select"
									value={option}
									onChange={handleOptionChange}
									sx={{
										borderRadius: '8px',
										height: '44px',
										'&:placeholder': {
											color: 'red',
										},
										'& .MuiSvgIcon-root': {
											right: i18n?.language === 'ar' ? 'unset' : '7px',
											left: i18n?.language === 'ar' ? '7px' : 'unset',
										},
									}}
								>
									<MenuItem value="Bug" className="rtl-able">
										{reportIssueTranslate('select.issueTypeSelect.bug')}
									</MenuItem>
									<MenuItem value="Suggest" className="rtl-able">
										{reportIssueTranslate('select.issueTypeSelect.improvement')}
									</MenuItem>
									<MenuItem value="Other" className="rtl-able">
										{reportIssueTranslate('select.issueTypeSelect.other')}
									</MenuItem>
								</Select>
							</FormControl>
						</Box>

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Typography sx={{ fontSize: '12px' }}>
								{reportIssueTranslate('text.issueCategorySelectTitle')}
							</Typography>

							<FormControl fullWidth size="small">
								<InputLabel
									shrink={false}
									id="select-label"
									sx={{ color: '#9FA6B3', right: i18n?.language === 'ar' ? '30px' : 'unset' }}
								>
									{category === '' && commonTranslate('inputs.reportIssueCategoryPlaceholder')}
								</InputLabel>
								<Select
									data-testid="category-select"
									labelId="select-label"
									id="select"
									value={category}
									onChange={handleCategoryChange}
									sx={{
										borderRadius: '8px',
										height: '44px',
										'& .MuiSvgIcon-root': {
											right: i18n?.language === 'ar' ? 'unset' : '7px',
											left: i18n?.language === 'ar' ? '7px' : 'unset',
										},
									}}
								>
									<MenuItem value="General Step" className="rtl-able">
										{reportIssueTranslate('select.issueCategorySelect.general')}
									</MenuItem>
									<MenuItem value="Usecases Step" className="rtl-able">
										{reportIssueTranslate('select.issueCategorySelect.useCase')}
									</MenuItem>
									<MenuItem value="Test Cases Step" className="rtl-able">
										{reportIssueTranslate('select.issueCategorySelect.testCase')}
									</MenuItem>
									<MenuItem value="Notifications Step" className="rtl-able">
										{reportIssueTranslate('select.issueCategorySelect.notifications')}
									</MenuItem>
									<MenuItem value="Report Step" className="rtl-able">
										{reportIssueTranslate('select.issueCategorySelect.report')}
									</MenuItem>
								</Select>
							</FormControl>
						</Box>

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								position: 'relative',
							}}
						>
							<Typography sx={{ fontSize: '12px' }}>
								{reportIssueTranslate('text.issueMessage')}
							</Typography>
							<FormControl fullWidth>
								<TextField
									multiline
									inputProps={{
										style: {
											overflow: 'auto',
											height: '128px',
											width: 'calc(100% - 35px)',
										},
										sx: {
											'&::placeholder': {
												color: '#9FA6B3',
												opacity: '1',
											},
										},
										maxLength: 10000,
									}}
									placeholder={reportIssueTranslate('text.issueMessageInputPlaceholder') as string}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
										},
									}}
									{...register('message')}
								/>
							</FormControl>
							<Button
								sx={{
									minWidth: '0',
									position: 'absolute',
									right: i18n?.language == 'ar' ? 'unset' : '12px',
									left: i18n?.language == 'ar' ? '12px' : 'unset',
									top: '30px',
									padding: 0,
								}}
							>
								<input
									type="file"
									accept="image/*"
									multiple
									style={{ display: 'none' }}
									id="file-upload"
									data-testid="file-upload-input"
									onChange={handleFileChange}
								/>
								<label
									htmlFor="file-upload"
									style={{
										lineHeight: '0',
										cursor: 'pointer',
									}}
								>
									<DocumentUpload />
								</label>
							</Button>
						</Box>

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
								overflow: 'auto',
							}}
						>
							{selectedFiles.length > 0 &&
								selectedFiles.map((file, index) => {
									return (
										<Box
											key={index}
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												padding: '4px 8px',
												background: '#F3F4F5',
												borderRadius: '8px',
											}}
										>
											<Typography sx={{ fontSize: '14px' }}>{file.name}</Typography>
											<Button
												data-testid={`remove-file-button-${index}`}
												sx={{
													minWidth: '0',
													padding: '0',
												}}
												onClick={() => handleRemoveFile(index)}
											>
												<CloseSmall />
											</Button>
										</Box>
									);
								})}
						</Box>
					</Box>
				</Box>

				<Box
					className="not-rtl-able"
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						marginTop: 'auto',
						gap: '24px',
					}}
				>
					<Button
						sx={{
							marginTop: '16px',
							height: '44px',
							width: '50%',
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
						}}
						variant="outlined"
						color="primary"
						onClick={leaveReportAnIssue}
					>
						{commonTranslate('buttons.cancel')}
					</Button>

					{loading ? (
						<Button
							sx={{
								marginTop: '16px',
								height: '44px',
								width: '50%',
								borderRadius: '8px',
							}}
							variant="contained"
							color="primary"
						>
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#91B6FF"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						</Button>
					) : (
						<>
							<Button
								sx={{
									marginTop: '16px',
									height: '44px',
									width: '50%',
									borderRadius: '8px',
									textTransform: 'none',
									fontSize: '16px',
									fontWeight: '700',
								}}
								type="submit"
								variant="contained"
								color="primary"
							>
								{commonTranslate('buttons.submit')}
							</Button>
						</>
					)}
				</Box>
			</form>

			{isSuccessBugReportModal && (
				<SuccessBugReportModal handleCloseModal={handleCloseModal} resetForm={resetForm} />
			)}

			{isLeaveReportAnIssueModal && <LeaveReportAnIssueModal handleCloseModal={handleCloseModal} />}
		</Box>
	);
};
